<div class="flex flex-col gap-4">
  <section class="rounded-lg border !border-slate-200 bg-white p-4 shadow-sm">
    @let balance = getDealBalance(price, paidTotal);
    @let percent = getPaidPercent(price, paidTotal);
    <div class="flex flex-wrap items-start gap-x-8 gap-y-3">
      <div class="flex min-w-0 flex-col gap-0.5">
        <span class="text-xs font-medium text-slate-400">{{ 'board_payments.card.deal_amount' | translate }}</span>
        <span class="whitespace-nowrap text-lg font-bold text-slate-900">{{ formatMoneyAmount(price, currency) }}</span>
      </div>
      <div class="flex min-w-0 flex-col gap-0.5">
        <span class="text-xs font-medium text-slate-400">{{ 'board_payments.card.paid_total' | translate }}</span>
        <span class="whitespace-nowrap text-lg font-bold text-slate-900">{{
          formatMoneyAmount(paidTotal, currency)
        }}</span>
      </div>
      @if (balance.remaining > 0) {
        <div class="flex min-w-0 flex-col gap-0.5">
          <span class="text-xs font-medium text-slate-400">{{ 'board_payments.card.remaining' | translate }}</span>
          <span class="whitespace-nowrap text-lg font-bold text-slate-900">{{
            formatMoneyAmount(balance.remaining, currency)
          }}</span>
        </div>
      } @else if (balance.overpaid > 0) {
        <div class="flex min-w-0 flex-col gap-0.5">
          <span class="text-xs font-medium text-slate-400">{{ 'board_payments.card.overpaid' | translate }}</span>
          <span class="whitespace-nowrap text-lg font-bold text-violet-700">{{
            formatMoneyAmount(balance.overpaid, currency)
          }}</span>
        </div>
      }
      @if (paymentStatus; as status) {
        <span class="ml-auto rounded-full px-2.5 py-1 text-xs font-semibold" [ngClass]="dealStatusStyles[status].badge">
          {{ 'board_payments.deal_status.' + status | translate }}
        </span>
      }
    </div>
    <div class="mt-3 flex items-center gap-3">
      <div
        class="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        [attr.aria-valuenow]="percent"
        [attr.aria-label]="'board_payments.card.paid_total' | translate"
      >
        <div
          class="h-full rounded-full transition-all"
          [ngClass]="paymentStatus ? dealStatusStyles[paymentStatus].bar : 'bg-slate-300'"
          [style.width.%]="percent"
        ></div>
      </div>
      <span class="w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-500">{{ percent }}%</span>
    </div>
  </section>

  <section class="overflow-hidden rounded-lg border !border-slate-200 bg-white shadow-sm">
    <div class="flex items-center gap-2 px-4 py-3">
      <h3 class="!m-0 !text-sm !font-semibold !leading-5 text-slate-900">
        {{ 'board_payments.card.title' | translate }}
      </h3>
      <span class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{{
        payments.length
      }}</span>
      <app-button class="ml-auto" [customClass]="'!h-9'" (onChangeClick)="openForm(null)">
        <span class="flex items-center gap-1 text-sm font-semibold leading-none">
          <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
          {{ 'board_payments.card.add' | translate }}
        </span>
      </app-button>
    </div>

    <app-table-v2
      class="border-t !border-slate-100"
      layout="bare"
      minWidth="min-w-[760px]"
      actionsClass="w-32"
      [columns]="columns"
      [rows]="payments"
      [trackBy]="trackById"
      [actions]="actions"
      [rowClass]="rowClass"
      emptyIcon="payments"
      emptyText="board_payments.card.empty"
    >
      <ng-template appTableV2Cell="amount" [appTableV2CellOf]="payments" let-payment>
        <span class="font-semibold text-slate-900" [class.line-through]="payment.status === 'canceled'">{{
          formatMoneyAmount(payment.amount, currency)
        }}</span>
      </ng-template>
      <ng-template appTableV2Cell="payment_type" [appTableV2CellOf]="payments" let-payment>
        <ng-container
          [ngTemplateOutlet]="directoryItem"
          [ngTemplateOutletContext]="{ $implicit: payment.payment_type }"
        />
      </ng-template>
      <ng-template appTableV2Cell="payment_service" [appTableV2CellOf]="payments" let-payment>
        <ng-container
          [ngTemplateOutlet]="directoryItem"
          [ngTemplateOutletContext]="{ $implicit: payment.payment_service }"
        />
      </ng-template>
      <ng-template appTableV2Cell="fiscal" [appTableV2CellOf]="payments" let-payment>
        <app-payment-fiscal-status [payment]="payment" (changed)="reloadFiscal()" />
      </ng-template>
      <ng-template appTableV2Cell="status" [appTableV2CellOf]="payments" let-payment>
        <span
          class="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold"
          [ngClass]="statusClasses[payment.status]"
          [matTooltip]="payment.status === 'canceled' ? ('board_payments.card.canceled_hint' | translate) : ''"
        >
          {{ 'board_payments.status.' + payment.status | translate }}
        </span>
      </ng-template>
      <ng-template appTableV2Cell="date" [appTableV2CellOf]="payments" let-payment>
        {{ getPaymentDate(payment) | date: 'dd.MM.yyyy HH:mm' }}
      </ng-template>
      <ng-template appTableV2Cell="comment" [appTableV2CellOf]="payments" let-payment>
        @if (payment.comment) {
          <span class="block truncate text-slate-600" [title]="payment.comment">{{ payment.comment }}</span>
        } @else {
          <span class="text-slate-400">—</span>
        }
      </ng-template>
    </app-table-v2>
  </section>
</div>

<ng-template #directoryItem let-item>
  @if (item) {
    <span class="flex min-w-0 items-center gap-1.5">
      <span class="max-w-56 truncate" [title]="item.name">{{ item.name }}</span>
      @if (!item.is_active) {
        <span class="shrink-0 rounded bg-slate-100 px-1.5 text-xs font-semibold text-slate-500">{{
          'board_payments.inactive' | translate
        }}</span>
      }
    </span>
  } @else {
    <span class="text-slate-400">—</span>
  }
</ng-template>
